import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BottomNavigation from '../components/BottomNavigation';
import EnrollPopup from '../components/EnrollPopup';
import InterestsPopup from '../components/InterestsPopup';
import './Home.css';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [showEnrollPopup, setShowEnrollPopup] = useState(false);
  const [showInterestsPopup, setShowInterestsPopup] = useState(false);

  useEffect(() => {
    // Check if user just registered and needs to see interests popup
    const shouldShowInterests = sessionStorage.getItem('showInterests') === 'true';
    if (shouldShowInterests) {
      setShowInterestsPopup(true);
      sessionStorage.removeItem('showInterests');
    }
  }, []);

  const handleInterestsSubmit = (interests: string[]) => {
    console.log('Selected interests:', interests);
    // Save interests to backend/localStorage
    localStorage.setItem('userInterests', JSON.stringify(interests));
  };

  const categories = [
    { id: 1, name: 'English', icon: '🇬🇧' },
    { id: 2, name: 'Math', icon: '🔢' },
    { id: 3, name: 'Science', icon: '🔬' },
    { id: 4, name: 'History', icon: '📜' },
    { id: 5, name: 'Art', icon: '🎨' },
    { id: 6, name: 'Spanish', icon: '🇪🇸' }
  ];

  const popularCourses = [
    { id: 1, title: 'Advanced Mathematics', instructor: 'Dr. Smith', rating: 4.8, students: 1200, price: '$49' },
    { id: 2, title: 'English Literature', instructor: 'Prof. Johnson', rating: 4.9, students: 980, price: '$39' },
    { id: 3, title: 'Computer Science Basics', instructor: 'Dr. Williams', rating: 4.7, students: 1500, price: '$59' },
    { id: 4, title: 'Spanish for Beginners', instructor: 'Ms. Garcia', rating: 4.6, students: 850, price: '$29' },
    { id: 5, title: 'Physics Fundamentals', instructor: 'Dr. Brown', rating: 4.8, students: 1100, price: '$45' },
    { id: 6, title: 'Chemistry Basics', instructor: 'Prof. Davis', rating: 4.7, students: 920, price: '$42' }
  ];

  // Split courses into rows of 2
  type Course = typeof popularCourses[0];
  const courseRows: Course[][] = [];
  for (let i = 0; i < popularCourses.length; i += 2) {
    courseRows.push(popularCourses.slice(i, i + 2));
  }

  const popularResources = [
    { id: 1, title: 'Mathematics Fundamentals', author: 'John Doe', type: '📖 Book', rating: 4.8 },
    { id: 2, title: 'English Grammar Guide', author: 'Jane Smith', type: '📖 Book', rating: 4.9 },
    { id: 3, title: 'Science Encyclopedia', author: 'Dr. Brown', type: '📖 Book', rating: 4.7 },
    { id: 4, title: 'History Timeline', author: 'Prof. Davis', type: '📖 Book', rating: 4.6 }
  ];

  const onlineCourses = [
    { id: 1, title: 'Advanced Mathematics Live Course', instructor: 'Dr. Smith', sessions: 24, platform: 'BBB', enrolled: 1200 },
    { id: 2, title: 'English Conversation Course', instructor: 'Ms. Johnson', sessions: 18, platform: 'Google Meet', enrolled: 850 },
    { id: 3, title: 'Science Lab Course', instructor: 'Dr. Williams', sessions: 20, platform: 'BBB', enrolled: 650 },
    { id: 4, title: 'Spanish Practice Course', instructor: 'Ms. Garcia', sessions: 16, platform: 'Google Meet', enrolled: 420 }
  ];

  return (
    <div className="home-page">
      {/* Header with Search and Notifications */}
      <header className="home-header">
        <button className="profile-button" onClick={() => navigate('/profile')}>
          <span className="profile-icon">👤</span>
        </button>
        <div className="search-container">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search courses, books..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
        <button className="notification-button">
          <span className="notification-icon">🔔</span>
          <span className="notification-badge">3</span>
        </button>
      </header>

      <div className="home-content">
        {/* Categories Section */}
        <section className="categories-section">
          <div className="categories-slider">
            {categories.map((category) => (
              <div key={category.id} className="category-item" onClick={() => navigate('/categories')}>
                <div className="category-icon">{category.icon}</div>
                <span className="category-name">{category.name}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Slider Section */}
        <section className="slider-section">
          <div className="slider-container">
            <div className="slide active">
              <div className="slide-content">
                <h3>Special Offer</h3>
                <p>Get 50% off on all courses this month!</p>
                <button className="slide-button">Learn More</button>
              </div>
            </div>
            <div className="slider-dots">
              <span className="dot active"></span>
              <span className="dot"></span>
              <span className="dot"></span>
            </div>
          </div>
        </section>

        {/* Most Popular Courses */}
        <section className="courses-section">
          <div className="section-header">
            <h2 className="section-title">Most Popular Courses</h2>
            <button className="see-all-button" onClick={() => navigate('/courses/all')}>See All</button>
          </div>
          <div className="courses-slider">
            {courseRows.map((row, rowIndex) => (
              <div key={rowIndex} className="courses-slider-row">
                {row.map((course) => (
                  <div key={course.id} className="course-card" onClick={() => navigate(`/course/${course.id}`)}>
                    <div className="course-image">
                      <div className="course-placeholder">📚</div>
                    </div>
                    <div className="course-info">
                      <h3 className="course-title">{course.title}</h3>
                      <p className="course-instructor">{course.instructor}</p>
                      <div className="course-meta">
                        <span className="course-rating">⭐ {course.rating}</span>
                        <span className="course-students">👥 {course.students}</span>
                      </div>
                      <div className="course-footer">
                        <span className="course-price">{course.price}</span>
                        <button 
                          className="enroll-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowEnrollPopup(true);
                          }}
                        >
                          Enroll
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </section>

        {/* Online Courses */}
        <section className="online-courses-section">
          <div className="section-header">
            <h2 className="section-title">Online Courses</h2>
            <button className="see-all-button" onClick={() => navigate('/live-sessions/all')}>See All</button>
          </div>
          <div className="online-courses-slider">
            {onlineCourses.map((course) => (
              <div key={course.id} className="online-course-card" onClick={() => navigate(`/online-course/${course.id}`)}>
                <div className="online-course-header">
                  <div className="platform-badge">{course.platform}</div>
                  <div className="enrolled-count">👥 {course.enrolled}</div>
                </div>
                <h3 className="online-course-title">{course.title}</h3>
                <p className="online-course-instructor">{course.instructor}</p>
                <div className="online-course-sessions">🔴 {course.sessions} sessions</div>
                <button 
                  className="join-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowEnrollPopup(true);
                  }}
                >
                  Enroll Course
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Most Popular Resources */}
        <section className="resources-section">
          <div className="section-header">
            <h2 className="section-title">Most Popular Resources</h2>
            <button className="see-all-button">See All</button>
          </div>
          <div className="resources-slider">
            {popularResources.map((resource) => (
              <div key={resource.id} className="resource-card" onClick={() => navigate(`/resource/${resource.id}`)}>
                <div className="resource-icon">{resource.type}</div>
                <div className="resource-info">
                  <h3 className="resource-title">{resource.title}</h3>
                  <p className="resource-author">{resource.author}</p>
                  <div className="resource-rating">⭐ {resource.rating}</div>
                </div>
                <button 
                  className="pay-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowEnrollPopup(true);
                  }}
                >
                  Pay
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Bottom spacing for navigation */}
        <div className="bottom-spacer"></div>
      </div>

      <EnrollPopup 
        isOpen={showEnrollPopup} 
        onClose={() => setShowEnrollPopup(false)} 
      />
      <InterestsPopup
        isOpen={showInterestsPopup}
        onClose={() => setShowInterestsPopup(false)}
        onSubmit={handleInterestsSubmit}
      />
      <BottomNavigation />
    </div>
  );
};

export default Home;

